import * as shoppingCartLS from '@/lib/shoppingCartLS'

/**
 * Configuración del debounce para prevenir llamadas duplicadas a la API
 */
const DEBOUNCE_TIME = 500 // Tiempo en milisegundos entre llamadas
const lastCallTime = {} // Registro de última llamada por acción y parámetros

/**
 * Estado del módulo de carrito
 */
export const state = () => ({
  currentAddedItems: 0, // Contador de items agregados recientemente
})

/**
 * Mutaciones del módulo de carrito
 */
export const mutations = {
  /**
   * Actualiza el contador de items agregados
   * @param {Object} state - Estado actual
   * @param {Number} payload - Nuevo valor del contador
   */
  updateCurrentAddedItems(state, payload) {
    state.currentAddedItems = payload
  },
}

/**
 * Acciones del módulo de carrito
 */
export const actions = {
  /**
   * Obtiene un nuevo ID de carrito
   * @returns {Promise} Respuesta de la API
   */
  async getCartId(state) {
    const res = await this.$axios.get('/shopping-cart/id')
    return res
  },

  /**
   * Inicializa un nuevo carrito de compras
   * @returns {Promise} Respuesta de la API
   */
  async initShoppingCart(state) {
    const _cartId = await this.$axios.get('/shopping-cart/id')
    shoppingCartLS.setCartId(_cartId.data.id)
    shoppingCartLS.setProducts([])
  },

  /**
   * Obtiene el contenido del carrito
   * @returns {Promise} Respuesta de la API
   */
  async getShoppingCartContent(state) {
    const res = await this.$axios.get('/pages/shopping-cart')
    return res
  },

  /**
   * Calcula el costo de envío
   * @param {Object} data - Datos para el cálculo
   * @param {String} data.id - ID del carrito
   * @param {Object} data.data - Información de envío
   * @returns {Promise} Respuesta de la API
   */
  async calculateShipping(state, data) {
    const res = await this.$axios.post(
      'purchases/calculate-shipping?cart_id=' + data.id,
      data.data
    )
    return res
  },

  /**
   * Obtiene el contenido del carrito por ID
   * @param {String} id - ID del carrito
   * @returns {Promise} Respuesta de la API
   */
  async getContent(state, id) {
    const res = await this.$axios.get('/shopping-cart?cart_id=' + id)
    return res
  },

  /**
   * Obtiene los métodos de pago disponibles
   * @param {String} id - ID del carrito
   * @returns {Promise} Respuesta de la API
   */
  async getPayments(state, id) {
    const res = await this.$axios.get('/purchases/' + id)
    return res
  },

  /**
   * Obtiene las direcciones guardadas
   * @param {String} id - ID del carrito
   * @returns {Promise} Respuesta de la API
   */
  async getAddresses(state, id) {
    const res = await this.$axios.get('/purchases/address/' + id)
    return res
  },

  /**
   * Agrega una nueva dirección
   * @param {Object} data - Datos de la dirección
   * @returns {Promise} Respuesta de la API
   */
  async addNewAddreses(state, data) {
    const res = await this.$axios.post('/purchases/address', data)
    return res
  },

  /**
   * Verifica la disponibilidad de códigos de descuento automáticos
   * @param {Object} data - Datos para la verificación
   * @param {String} data.cartId - ID del carrito
   * @param {String} data.mail - Email del usuario
   * @returns {Promise} Respuesta de la API
   */
  async autoCodeAvailability(state, data) {
    const res = await this.$axios.get(
      `/discount-codes/auto-apply?cart_id=${data.cartId}&email=${data.mail}`
    )
    return res
  },

  /**
   * Actualiza el contenido del carrito con debounce
   * @param {Object} data - Datos para la actualización
   * @param {String} data.cartId - ID del carrito
   * @param {Object} data.product - Producto a actualizar
   * @returns {Promise} Respuesta de la API
   */
  async updateCart(state, data) {
    const now = Date.now()
    const key = `updateCart_${data.cartId}_${data.product.sku}`

    // Si la última llamada fue hace menos de DEBOUNCE_TIME ms, no hacemos nada
    if (lastCallTime[key] && now - lastCallTime[key] < DEBOUNCE_TIME) {
      return { data: { success: true } }
    }

    lastCallTime[key] = now
    state.commit('updateCurrentAddedItems', 0)
    const res = await this.$axios.put(
      '/shopping-cart?cart_id=' + data.cartId,
      data.product
    )
    return res
  },

  /**
   * Aplica un código de descuento
   * @param {Object} data - Datos para la aplicación del descuento
   * @param {String} data.id - ID del carrito
   * @param {Object} data.data - Datos del descuento
   * @returns {Promise} Respuesta de la API
   */
  async redemDiscount(state, data) {
    const res = await this.$axios.post(
      '/discount-codes/redeem?cart_id=' + data.id,
      data.data
    )
    return res
  },

  /**
   * Inicio de sesión simple
   * @param {Object} data - Credenciales de usuario
   * @returns {Promise} Respuesta de la API
   */
  async simpleLogin(state, data) {
    const res = await this.$axios.post('login', data)
    return res
  },

  /**
   * Registro como invitado
   * @param {Object} data - Datos del usuario invitado
   * @returns {Promise} Respuesta de la API
   */
  async registerAsGuest(state, data) {
    const res = await this.$axios.post('/purchases/register-as-guest', data)
    // console.log('res', res)
    return res
  },

  /**
   * Crea una nueva orden
   * @param {Object} data - Datos de la orden
   * @param {String} data.id - ID del carrito
   * @param {Object} data.body - Detalles de la orden
   * @returns {Promise} Respuesta de la API
   */
  async createOrder(state, data) {
    const res = await this.$axios.post(
      '/purchases?cart_id=' + data.id,
      data.body
    )
    return res
  },
}
